/* Figure Quality Validator — client-side PNG inspection.
 * Checks each base64-encoded chart for:
 *   - minimum resolution (width ≥ 600 px for publication)
 *   - sensible aspect ratio
 *   - file size (too small = blank; too large = bloated)
 *   - PNG signature integrity (not corrupt)
 *   - blank-canvas detection (nearly uniform colour = likely empty)
 * Returns a per-figure report + aggregate summary. No network.
 */

const PNG_SIG = [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A];

function b64ToBytes(b64) {
  try {
    const bin = atob(b64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  } catch {
    return null;
  }
}

/* Parse PNG header: IHDR is always at offset 16 (8-byte sig + 4-byte length + 4-byte 'IHDR'). */
function parsePngHeader(bytes) {
  if (!bytes || bytes.length < 24) return null;
  for (let i = 0; i < PNG_SIG.length; i++) {
    if (bytes[i] !== PNG_SIG[i]) return null;
  }
  const width = (bytes[16] << 24) | (bytes[17] << 16) | (bytes[18] << 8) | bytes[19];
  const height = (bytes[20] << 24) | (bytes[21] << 16) | (bytes[22] << 8) | bytes[23];
  const bitDepth = bytes[24];
  const colorType = bytes[25];
  return { width, height, bitDepth, colorType };
}

/* Quick blank-detection via Image pixel sampling. Async because we decode
 * the PNG via an off-screen <img> + canvas to inspect pixel variance. */
async function inspectPixels(b64, maxSampleSize = 64) {
  return new Promise(resolve => {
    const img = new window.Image();
    img.onload = () => {
      try {
        const cnv = document.createElement('canvas');
        const w = Math.min(img.width, maxSampleSize);
        const h = Math.min(img.height, maxSampleSize);
        cnv.width = w; cnv.height = h;
        const ctx = cnv.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        const data = ctx.getImageData(0, 0, w, h).data;
        // Compute variance across luminance
        const lums = [];
        for (let i = 0; i < data.length; i += 4) {
          lums.push(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]);
        }
        const mean = lums.reduce((a, b) => a + b, 0) / lums.length;
        const variance = lums.reduce((a, b) => a + (b - mean) ** 2, 0) / lums.length;
        resolve({ variance, meanLum: mean, uniquePixels: new Set(lums.map(l => Math.round(l / 10))).size });
      } catch {
        resolve({ variance: null });
      }
    };
    img.onerror = () => resolve({ variance: null });
    img.src = `data:image/png;base64,${b64}`;
  });
}

/* Validate one figure. Returns { id, grade, issues, dimensions, sizeKB, ... }. */
export async function validateFigure(figure) {
  const b64 = figure.base64 || '';
  const sizeKB = Math.round((b64.length * 0.75) / 1024);
  const issues = [];
  let dimensions = null;

  const bytes = b64ToBytes(b64);
  if (!bytes) {
    issues.push({ level: 'critical', msg: 'PNG decoding failed — possibly corrupt base64.' });
  } else {
    dimensions = parsePngHeader(bytes);
    if (!dimensions) {
      issues.push({ level: 'critical', msg: 'Invalid PNG signature or header — image is not a valid PNG.' });
    } else {
      // Resolution
      if (dimensions.width < 600) {
        issues.push({
          level: dimensions.width < 400 ? 'critical' : 'warn',
          msg: `Low width (${dimensions.width}px). Publication standard ≥ 600px (~150 DPI at 4 inches).`,
        });
      }
      // Aspect ratio
      const ratio = dimensions.width / Math.max(dimensions.height, 1);
      if (ratio > 4 || ratio < 0.3) {
        issues.push({ level: 'warn', msg: `Extreme aspect ratio (${ratio.toFixed(2)}) — figure may be squashed.` });
      }
    }
  }

  // File size bounds
  if (sizeKB < 5) {
    issues.push({ level: 'critical', msg: `Very small file (${sizeKB} KB) — figure likely blank or empty.` });
  } else if (sizeKB > 2048) {
    issues.push({ level: 'warn', msg: `Large file (${sizeKB} KB) — consider further compression.` });
  }

  // Pixel inspection (blank canvas)
  let pixelStats = { variance: null };
  if (bytes && dimensions) {
    pixelStats = await inspectPixels(b64);
    if (pixelStats.variance !== null && pixelStats.variance < 4) {
      issues.push({ level: 'critical', msg: 'Nearly uniform colour — figure appears blank.' });
    }
    if (pixelStats.variance !== null && pixelStats.variance >= 4 && pixelStats.variance < 50) {
      issues.push({ level: 'warn', msg: 'Low pixel variance — figure may have very little content.' });
    }
  }

  // Grade
  const hasCritical = issues.some(i => i.level === 'critical');
  const hasWarn = issues.some(i => i.level === 'warn');
  const grade = hasCritical ? 'fail' : hasWarn ? 'warn' : 'pass';

  return {
    id: figure.id,
    caption: figure.caption,
    section: figure.section,
    sizeKB,
    dimensions: dimensions || { width: null, height: null },
    variance: pixelStats.variance,
    issues,
    grade,
  };
}

/* Validate a batch and return aggregate summary. */
export async function validateAllFigures(figures) {
  const reports = await Promise.all(figures.map(validateFigure));
  const pass = reports.filter(r => r.grade === 'pass').length;
  const warn = reports.filter(r => r.grade === 'warn').length;
  const fail = reports.filter(r => r.grade === 'fail').length;
  const totalSizeKB = reports.reduce((s, r) => s + r.sizeKB, 0);
  const overall = fail > 0 ? 'fail' : warn > 0 ? 'warn' : 'pass';
  return { reports, pass, warn, fail, total: reports.length, totalSizeKB, overall };
}
